import { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { DAYS_ABBREVIATIONS } from '../../constants/weekDayConstants';
import { TrashCanIcon, PlusIcon } from '../../assets/svgs/icons';

export interface HoursEntry {
  days: boolean[];
  startTime: string;
  endTime: string;
}

interface EditLocationHoursModalProps {
  isOpen: boolean;
  data?: HoursEntry[] | null;
  onCancel: () => void;
  onSave: (entries: HoursEntry[]) => void;
}

const DAYS = Object.keys(DAYS_ABBREVIATIONS);

const emptyEntry = (): HoursEntry => ({
  days: Object.values(DAYS_ABBREVIATIONS),
  startTime: '',
  endTime: ''
});

const MODAL_WIDTH_OVERRIDE = `
  .location-hours-modal {
    width: 700px !important;
  }
`;

const EditLocationHoursModal = ({ isOpen, data, onCancel, onSave }: EditLocationHoursModalProps) => {
  const [entries, setEntries] = useState<HoursEntry[]>([emptyEntry()]);

  useEffect(() => {
    if (data && data.length > 0) {
      setEntries(data);
    } else {
      setEntries([emptyEntry()]);
    }
  }, [data]);

  if (!isOpen) {
    return null;
  }

  const toggleDay = (entryIndex: number, dayIndex: number) => {
    const updated = [...entries];
    updated[entryIndex] = {
      ...updated[entryIndex],
      days: updated[entryIndex].days.map((d, i) => (i === dayIndex ? !d : d))
    };
    setEntries(updated);
  };

  const updateTime = (entryIndex: number, field: 'startTime' | 'endTime', value: string) => {
    const updated = [...entries];
    updated[entryIndex] = { ...updated[entryIndex], [field]: value };
    setEntries(updated);
  };

  const addEntry = () => {
    setEntries([...entries, emptyEntry()]);
  };

  const removeEntry = (index: number) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  return (
    <Modal onOverlayClicked={onCancel} className="location-hours-modal">
      <style>{'.location-hours-modal { width: 700px !important; }'}</style>
      <div className="modal-header">
        <h1 className="modal-title">EDIT HOURS</h1>
      </div>

      <div style={{ padding: '16px 20px' }}>
        {/* Column headers */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', marginBottom: '12px' }}>
          <div style={{ flex: '0 0 auto' }}>
            <div style={{ fontSize: '0.8125rem', fontWeight: 700, letterSpacing: '0.96px' }}>DAYS OF WEEK</div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <div style={{ width: '100px', fontSize: '0.8125rem', fontWeight: 700, letterSpacing: '0.96px' }}>
              START TIME
            </div>
            <div style={{ width: '100px', fontSize: '0.8125rem', fontWeight: 700, letterSpacing: '0.96px' }}>
              END TIME
            </div>
            <div style={{ width: '30px' }} />
          </div>
        </div>

        {/* Rows */}
        {entries.map((entry, entryIndex) => (
          <div key={entryIndex} style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{ display: 'flex', gap: '6px' }}>
                {DAYS.map((day, dayIndex) => (
                  <button
                    key={day}
                    type="button"
                    onClick={() => toggleDay(entryIndex, dayIndex)}
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      backgroundColor: entry.days[dayIndex] ? 'var(--color-accent)' : '#e0e0e0',
                      color: entry.days[dayIndex] ? 'white' : '#666'
                    }}
                  >
                    {day}
                  </button>
                ))}
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="7:00 AM"
                  value={entry.startTime}
                  onChange={(e) => updateTime(entryIndex, 'startTime', e.target.value)}
                  style={{
                    width: '100px',
                    height: '35px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '0 8px',
                    fontSize: '0.75rem'
                  }}
                />
                <input
                  type="text"
                  placeholder="10:00 PM"
                  value={entry.endTime}
                  onChange={(e) => updateTime(entryIndex, 'endTime', e.target.value)}
                  style={{
                    width: '100px',
                    height: '35px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '0 8px',
                    fontSize: '0.75rem'
                  }}
                />
                <button
                  type="button"
                  onClick={entryIndex === 0 ? addEntry : () => removeEntry(entryIndex)}
                  style={{
                    width: '30px',
                    height: '35px',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {entryIndex === 0 ? (
                    <span style={{ transform: 'scale(0.7)', display: 'flex' }}>
                      <PlusIcon />
                    </span>
                  ) : (
                    <TrashCanIcon />
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 20px 20px' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            width: '140px',
            height: '35px',
            backgroundColor: 'var(--color-danger)',
            color: 'white',
            borderRadius: '999px',
            border: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '1.4px',
            cursor: 'pointer'
          }}
        >
          CANCEL
        </button>
        <button
          type="button"
          // TODO: Wire up to API call to update location hours
          onClick={() => onSave(entries)}
          style={{
            width: '140px',
            height: '35px',
            backgroundColor: 'var(--color-accent)',
            color: 'white',
            borderRadius: '999px',
            border: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '1.4px',
            cursor: 'pointer'
          }}
        >
          SAVE
        </button>
      </div>
    </Modal>
  );
};

export default EditLocationHoursModal;
